import { useSelector } from 'react-redux';
import React, { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useDrag } from 'react-dnd';
import { types as coreTypes } from '@sitebuilder/core';
import { searchGreySvg, grabSvg } from '../../assets/images';
import { StateShape as ReduxStateShape } from '../../reducers';
import Layers from './Layers';

export interface Layer {
    id: string;
    widgetId: string;
    widgetAltName?: string;
    children?: Layer[] | any;
    parentId?: string;
    contentPlaceHolders?: { [key: string]: string[] };
}

export const getLayers = (
    document: coreTypes.Document | null,
    widgetInstances: coreTypes.WidgetInstance[] | null,
    root: string
) => {
    const layer: any = {};
    widgetInstances?.forEach(
        ({ id, widgetId, parentId, properties, contentPlaceHolders }: coreTypes.WidgetInstance) => {
            const parent = document?.widgetInstances[parentId];
            Object.assign((layer[id] = layer[id] || {}), {
                id,
                widgetId,
                widgetAltName: properties?.widgetAltName,
                parentId: parentId || root,
                contentPlaceHolders: contentPlaceHolders || {},
            });
            layer[parentId] = layer[parentId] || {};

            if (parent && Object.keys(parent.contentPlaceHolders)[0] !== 'children') {
                const childPlaceholder: string =
                    Object.keys(parent.contentPlaceHolders).find((key) =>
                        parent.contentPlaceHolders[key].includes(id)
                    ) || '';
                layer[parentId].children = {
                    ...layer[parentId].children,
                    [childPlaceholder]: layer[parentId].children?.[childPlaceholder] || [],
                };

                const childIndex = parent?.contentPlaceHolders?.[childPlaceholder]?.findIndex(
                    (ch: string) => ch === id
                );

                if (childIndex > -1) {
                    layer[parentId].children[childPlaceholder][childIndex] = layer[id];
                } else {
                    layer[parentId].children?.[childPlaceholder].push(layer[id]);
                }
            } else {
                layer[parentId].children = layer[parentId].children || [];
                const childIndex = parent?.contentPlaceHolders.children?.findIndex(
                    (ch: string) => ch === id
                );

                if (childIndex > -1) {
                    layer[parentId].children[childIndex] = layer[id];
                } else {
                    layer[parentId].children?.push(layer[id]);
                }
            }
        }
    );

    return layer[root]?.children;
};

export const searchLayer = (data: Layer[], text: string) => {
    const getNodes = (result: Layer[], node: Layer) => {
        if (node.widgetId.toLowerCase().includes(text.toLowerCase())) {
            result.push(node);
            return result;
        }

        if (Array.isArray(node.children)) {
            const children = node.children.reduce(getNodes, []);

            if (children.length) result.push({ ...node, children });
        } else if (node.children && Object.keys(node.children).length > 0) {
            Object.keys(node.children).forEach((ch) => {
                const children = node.children[ch].reduce(getNodes, []);

                if (children.length) result.push({ ...node, children: { [ch]: [...children] } });
            });
        }

        return result;
    };

    return data.reduce(getNodes, []);
};

const LayerDragIcon = ({ widgetUUID }: { widgetUUID: string }) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [isCopyComponent, setIsCopyComponent] = useState(false);

    useEffect(() => {
        const handlePositionUpdate = (event: CustomEvent) => {
            const { top, left } = event.detail;
            setPosition({ top, left });
        };

        window.addEventListener('layer-menu-position', handlePositionUpdate as EventListener);

        return () => {
            window.removeEventListener(
                'layer-menu-position',
                handlePositionUpdate as EventListener
            );
        };
    }, []);

    useEffect(() => {
        const el = document.getElementById(`layer-${widgetUUID}`);
        const layersList = document.getElementById('layers-list');

        if (el && layersList) {
            const rect = el.getBoundingClientRect();
            const layersListRect = layersList.getBoundingClientRect();

            setPosition({
                top: rect.top - layersListRect.top + 45,
                left: rect.left - layersListRect.left + rect.width - 15,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widgetUUID]);

    useHotkeys('ctrl+c', () => setIsCopyComponent(true), { keydown: true, keyup: false });
    useHotkeys('ctrl+c', () => setIsCopyComponent(false), { keyup: true, keydown: false });

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: 'WIDGET_LAYER',
            item: () => {
                return {
                    id: '',
                    widgetUUID,
                    dragMode: isCopyComponent ? 'copy' : 'move',
                    isRefComponent: false,
                    isPublicRefComponent: false,
                };
            },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [widgetUUID, isCopyComponent]
    );

    return (
        <div
            className="layer-drag-icon"
            ref={drag}
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                display: isDragging ? 'none' : 'flex',
            }}
        >
            <img src={grabSvg} alt="Drag icon" />
        </div>
    );
};

const LayersSidebar = () => {
    const layersRef = useRef<HTMLDivElement>(null);

    const { document: doc, selectedId } = useSelector((state: ReduxStateShape) => ({
        ...state?.templateEditor,
    }));

    const widgetInstances: coreTypes.WidgetInstance[] | null = doc && [
        ...Object.values(doc?.widgetInstances || {}),
    ];

    const initialLayers: Layer[] = getLayers(doc, widgetInstances, '');

    const [layers, setLayers] = useState<Layer[]>(initialLayers);
    const [searchValue, setSearchValue] = useState<string>('');

    const handleOnSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        if (!value.trim()) {
            setLayers(initialLayers);
        } else {
            const filteredLayers = searchLayer(initialLayers, value.trim());
            setLayers(filteredLayers);
        }

        setSearchValue(value);
    };

    useEffect(() => {
        setLayers(initialLayers);
        setSearchValue('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc?.widgetInstances]);

    useEffect(() => {
        const handleScroll = () => {
            if (!selectedId) return;

            const el = document.getElementById(`layer-${selectedId}`);
            const layersList = layersRef.current;

            if (el && layersList) {
                const elRect = el.getBoundingClientRect();
                const layersListRect = layersList.getBoundingClientRect();

                const top = elRect.top - layersListRect.top + 45;
                const left = elRect.left - layersListRect.left + elRect.width - 15;

                const event = new CustomEvent('layer-menu-position', {
                    detail: { top, left },
                });

                window.dispatchEvent(event);
            }
        };

        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [selectedId]);

    return (
        <div className="layers-sidebar">
            <div className="page-toolbar">
                <div className="input-search-widget">
                    <img src={searchGreySvg} alt="Search icon" />
                    <input
                        id="search-input"
                        type="text"
                        placeholder="All Layers"
                        value={searchValue}
                        onChange={handleOnSearch}
                        className={'input-textfield'}
                    />
                </div>
                <div id="layers-list" className="layers-list" ref={layersRef}>
                    {selectedId && selectedId !== 'root' && (
                        <LayerDragIcon widgetUUID={selectedId} />
                    )}
                    <Layers initialLayers={initialLayers} layers={layers} />
                </div>
            </div>
        </div>
    );
};

export default LayersSidebar;
